'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { Bell, CheckCircle2, AlertCircle, Loader2, X, Clock, Video, Download, Share2, ArrowRight, Film, ExternalLink } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useProcessingStatus } from '@/components/providers/processing-status-provider';
import { useRecentExports, RecentExport } from '@/lib/hooks/useRecentExports';
import { useActiveDomain } from '@/lib/hooks/useActiveDomain';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

export function ProcessingStatusBell() {
  const router = useRouter();
  const { activeDomain } = useActiveDomain();
  const { statuses, activeCount: processingActiveCount, clearCompleted } = useProcessingStatus();
  const {
    activeExports,
    completedExports,
    failedExports,
    activeCount: exportsActiveCount,
    refresh: refreshExports,
    dismissExport
  } = useRecentExports(activeDomain?.domain_id, 10);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [sharingId, setSharingId] = useState<string | null>(null);
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [hasNewExports, setHasNewExports] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const previousExportCount = useRef<number>(0);
  const seenExportIds = useRef<Set<string>>(new Set());

  // Combined active count (processing tasks + active exports)
  const totalActiveCount = processingActiveCount + exportsActiveCount;

  // Track new exports and trigger animation + auto-open
  useEffect(() => {
    if (completedExports.length > 0) {
      // Check for exports completed in the last 2 minutes (more recent = more likely new)
      const twoMinutesAgo = Date.now() - 2 * 60 * 1000;
      const newExports = completedExports.filter(exp => {
        const completedAt = exp.completed_at ? new Date(exp.completed_at).getTime() : 0;
        const isNew = completedAt > twoMinutesAgo && !seenExportIds.current.has(exp.id);
        return isNew;
      });

      if (newExports.length > 0) {
        setHasNewExports(true);
        // Auto-open dropdown when export completes
        setIsDropdownOpen(true);
        // Add to seen set
        newExports.forEach(exp => seenExportIds.current.add(exp.id));
        // Show toast for first new export
        if (newExports.length === 1) {
          toast.success(`Export ready: ${newExports[0].moment_title}`, {
            action: {
              label: 'Download',
              onClick: () => handleDownload(newExports[0]),
            },
          });
        } else {
          toast.success(`${newExports.length} exports ready!`);
        }
        // Refresh to ensure we have latest data
        refreshExports();
      }
    }
    previousExportCount.current = completedExports.length;
  }, [completedExports, refreshExports]);

  // Handle dropdown open/close
  const handleOpenChange = (open: boolean) => {
    setIsDropdownOpen(open);
    if (open && hasNewExports) {
      setHasNewExports(false);
    }
    // Refresh exports when opening dropdown
    if (open) {
      refreshExports();
    }
  };

  // Filter and sort statuses - ONLY show active/failed, completed exports go to Recent Exports section
  const activeStatuses = statuses.filter(s => s.stage !== 'complete');
  const sortedStatuses = [...activeStatuses].sort((a, b) => {
    if (a.stage === 'failed' && b.stage !== 'failed') return 1;
    if (a.stage !== 'failed' && b.stage === 'failed') return -1;
    return new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime();
  });

  const failedCount = statuses.filter(s => s.stage === 'failed').length;

  // Handle download
  const handleDownload = async (exp: RecentExport) => {
    setDownloadingId(exp.id);
    try {
      const domainParam = activeDomain?.domain_id ? `&domainId=${activeDomain.domain_id}` : '';
      const response = await fetch(`/api/moments/download-url?exportId=${exp.id}${domainParam}`);
      const data = await response.json();

      if (response.ok && data.downloadUrl) {
        window.open(data.downloadUrl, '_blank');
        toast.success('Download started');
      } else {
        toast.error(data.error || 'Failed to get download URL');
      }
    } catch (error) {
      console.error('[ProcessingStatusBell] Download error:', error);
      toast.error('Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  // Handle share
  const handleShare = async (exp: RecentExport) => {
    setSharingId(exp.id);
    try {
      const response = await fetch('/api/moments/share-export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ exportId: exp.id })
      });
      const data = await response.json();

      if (response.ok && data.shareUrl) {
        await navigator.clipboard.writeText(data.shareUrl);
        toast.success('Share link copied to clipboard!');
      } else {
        toast.error(data.error || 'Failed to generate share link');
      }
    } catch (error) {
      console.error('[ProcessingStatusBell] Share error:', error);
      toast.error('Failed to generate share link');
    } finally {
      setSharingId(null);
    }
  };

  // Navigate to post
  const handleNavigateToPost = (postId: string) => {
    router.push(`/dashboard/posts/${postId}/edit`);
  };

  // Dismiss an export notification
  const handleDismiss = async (exp: RecentExport) => {
    setDismissingId(exp.id);
    try {
      const success = await dismissExport(exp.id);
      if (success) {
        toast.success('Notification dismissed');
      } else {
        toast.error('Failed to dismiss');
      }
    } catch (error) {
      toast.error('Failed to dismiss');
    } finally {
      setDismissingId(null);
    }
  };

  // Show badge if there are active tasks, active exports, or new completed exports
  const showBadge = totalActiveCount > 0 || hasNewExports;
  const hasContent = sortedStatuses.length > 0 || activeExports.length > 0 || completedExports.length > 0 || failedExports.length > 0;

  return (
    <DropdownMenu open={isDropdownOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "relative h-9 w-9 transition-all",
            totalActiveCount > 0 && "animate-pulse",
            hasNewExports && "ring-2 ring-green-500 ring-offset-2 ring-offset-background"
          )}
          aria-label="Processing status notifications"
        >
          <Bell className={cn(
            "h-5 w-5 transition-colors",
            totalActiveCount > 0 && "text-blue-500 dark:text-blue-400",
            hasNewExports && "text-green-500 dark:text-green-400"
          )} />
          {totalActiveCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center animate-bounce"
            >
              {totalActiveCount}
            </Badge>
          )}
          {hasNewExports && totalActiveCount === 0 && (
            <Badge
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center animate-bounce bg-green-500 hover:bg-green-600"
            >
              <CheckCircle2 className="h-3 w-3" />
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[380px] max-h-[500px] overflow-y-auto">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Processing Status</span>
          <div className="flex gap-1">
            {(processingActiveCount > 0 || failedCount > 0) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  try {
                    // First clear from local state
                    clearCompleted();

                    // Then cleanup database
                    const response = await fetch('/api/processing-status/cleanup', {
                      method: 'POST',
                      credentials: 'include' // Ensure cookies are sent
                    });

                    if (response.ok) {
                      const result = await response.json();
                      console.log('[ProcessingStatus] Cleanup result:', result);
                      // Force a refresh to get clean state
                      setTimeout(() => window.location.reload(), 500);
                    } else {
                      console.error('Cleanup failed with status:', response.status);
                    }
                  } catch (error) {
                    console.error('Failed to cleanup:', error);
                    // Still reload to try to clear UI
                    window.location.reload();
                  }
                }}
                className="h-6 text-xs"
              >
                Clear all
              </Button>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {sortedStatuses.length === 0 && activeExports.length === 0 && completedExports.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            No active processing tasks or recent exports
          </div>
        ) : (
          <>
            {/* Active Processing Tasks (video editing, etc.) */}
            {sortedStatuses.length > 0 && (
              <>
                {sortedStatuses.map((status) => (
                  <ProcessingStatusItem key={status.sessionId} status={status} />
                ))}
              </>
            )}

            {failedCount > 0 && (
              <>
                <DropdownMenuSeparator />
                <div className="p-2 text-xs text-muted-foreground text-center">
                  {failedCount} failed task{failedCount !== 1 ? 's' : ''} - check logs for details
                </div>
              </>
            )}

            {/* Active Exports Section (pending/processing) */}
            {activeExports.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground font-normal flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Exporting ({activeExports.length})
                </DropdownMenuLabel>
                {activeExports.map((exp) => (
                  <div key={exp.id} className="px-3 py-2 border-b border-border/50 last:border-0 bg-blue-50/50 dark:bg-blue-950/20">
                    <div className="flex items-start gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-blue-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{exp.moment_title}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge variant="secondary" className="text-[10px] h-4 px-1.5">
                            {exp.status === 'pending' ? 'Queued' : 'Processing'}
                          </Badge>
                          <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                            {exp.quality.toUpperCase()}
                          </Badge>
                          {exp.isFullVideoExport && (
                            <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                              Full Video
                            </Badge>
                          )}
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          Started {formatDistanceToNow(new Date(exp.created_at), { addSuffix: true })}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                        onClick={() => handleDismiss(exp)}
                        disabled={dismissingId === exp.id}
                        title="Dismiss"
                      >
                        {dismissingId === exp.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <X className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Failed Exports Section */}
            {failedExports.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground font-normal flex items-center gap-2">
                  <AlertCircle className="h-3 w-3 text-destructive" />
                  Failed ({failedExports.length})
                </DropdownMenuLabel>
                {failedExports.map((exp) => (
                  <div key={exp.id} className="px-3 py-2 border-b border-border/50 last:border-0 bg-red-50/50 dark:bg-red-950/20">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{exp.moment_title}</p>
                        <p className="text-[10px] text-destructive mt-0.5 truncate">
                          {exp.errorMessage || 'Export failed - please try again'}
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          {formatDistanceToNow(new Date(exp.created_at), { addSuffix: true })}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                        onClick={() => handleDismiss(exp)}
                        disabled={dismissingId === exp.id}
                        title="Dismiss"
                      >
                        {dismissingId === exp.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <X className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Completed Exports Section */}
            {completedExports.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground font-normal flex items-center gap-2">
                  <Film className="h-3 w-3" />
                  Recent Exports
                </DropdownMenuLabel>
                <ScrollArea className="max-h-64">
                  {completedExports.map((exp) => (
                    <div key={exp.id} className="px-3 py-2 border-b border-border/50 last:border-0 hover:bg-muted/50">
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{exp.moment_title}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {exp.isFullVideoExport ? 'Full video export' : `from "${exp.post_title}"`}
                          </p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            {exp.completed_at && formatDistanceToNow(new Date(exp.completed_at), { addSuffix: true })}
                          </p>
                          <div className="flex items-center gap-1 mt-1.5">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 px-2 text-xs"
                              onClick={() => handleDownload(exp)}
                              disabled={downloadingId === exp.id}
                            >
                              {downloadingId === exp.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Download className="h-3 w-3" />
                              )}
                              <span className="ml-1 hidden sm:inline">Download</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 px-2 text-xs"
                              onClick={() => handleShare(exp)}
                              disabled={sharingId === exp.id}
                            >
                              {sharingId === exp.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Share2 className="h-3 w-3" />
                              )}
                              <span className="ml-1 hidden sm:inline">Share</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 px-2 text-xs"
                              onClick={() => handleNavigateToPost(exp.post_id)}
                              title="Go to post"
                            >
                              <ExternalLink className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </ScrollArea>
              </>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ProcessingStatusItem({ status }: { status: any }) {
  const getIcon = () => {
    switch (status.stage) {
      case 'complete':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'queued':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      default:
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
    }
  };

  const getTypeIcon = () => {
    switch (status.type) {
      case 'video_edit':
        return <Video className="h-3 w-3" />;
      default:
        return null;
    }
  };

  const isActive = !['complete', 'failed'].includes(status.stage);

  return (
    <div className={cn(
      "p-3 border-b last:border-0",
      !isActive && "opacity-60"
    )}>
      <div className="flex items-start gap-3">
        <div className="mt-1">{getIcon()}</div>
        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-2">
            {getTypeIcon()}
            <span className="text-sm font-medium">
              {status.type === 'video_edit' && 'Video Editing'}
              {status.type === 'transcript_edit' && 'Transcript Editing'}
              {status.type === 'export' && 'Export'}
            </span>
            <span className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(status.startedAt), { addSuffix: true })}
            </span>
          </div>

          <div className="text-sm text-muted-foreground">
            {status.message}
          </div>

          {isActive && (
            <div className="space-y-1">
              <Progress value={status.progress} className="h-1.5" />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{status.progress}%</span>
                {status.estimatedCompletion && (
                  <span>
                    Est. {formatDistanceToNow(new Date(status.estimatedCompletion), { addSuffix: true })}
                  </span>
                )}
              </div>
            </div>
          )}

          {status.error && (
            <div className="text-xs text-red-500 mt-1">
              {status.error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}